// /app/frontend/src/components/handshake/HandshakeScanner.js
// Scanner de QR Code para o sistema de Handshake Digital
import React, { useState, useEffect, useRef } from 'react';
import { 
  FiCamera, FiX, FiCheck, FiAlertTriangle, 
  FiMapPin, FiNavigation, FiShield, FiType
} from 'react-icons/fi';
import api from '@/services/api';

const HandshakeScanner = ({ 
  orderId, 
  step, // 'pickup_store', 'pickup_product', 'dropoff_arrive', 'dropoff_client', 'dropoff_product_client'
  onSuccess, 
  onCancel,
  storeLocation,
  clientLocation
}) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [distance, setDistance] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [useManualInput, setUseManualInput] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Calcular distância entre dois pontos (Haversine) - Definido antes do useEffect
  const calculateDistance = (loc1, loc2) => {
    const R = 6371000; // Raio da Terra em metros
    const dLat = (loc2.lat - loc1.lat) * Math.PI / 180;
    const dLon = (loc2.lng - loc1.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(loc1.lat * Math.PI / 180) * Math.cos(loc2.lat * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Obter localização atual
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocalização não suportada pelo navegador');
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setCurrentLocation(loc);
        setLocationError('');

        // Calcular distância
        const targetLocation = step.startsWith('pickup') ? storeLocation : clientLocation;
        if (targetLocation) {
          const dist = calculateDistance(loc, targetLocation);
          setDistance(Math.round(dist));
        }
      },
      (err) => {
        setLocationError('Não foi possível obter sua localização. Verifique as permissões do GPS.');
        console.error('Erro de geolocalização:', err);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [step, storeLocation, clientLocation]);

  // Iniciar câmera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setScanning(true);
      setError('');
      
      // Iniciar escaneamento
      requestAnimationFrame(scanQRCode);
    } catch (err) {
      setError('Não foi possível acessar a câmera. Verifique as permissões.');
      console.error('Erro ao acessar câmera:', err);
    }
  };

  // Parar câmera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  // Escanear QR Code do vídeo
  const scanQRCode = async () => {
    if (!scanning || !videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      // Usar biblioteca jsQR se disponível, senão usar BarcodeDetector
      if ('BarcodeDetector' in window) {
        try {
          const barcodeDetector = new BarcodeDetector({ formats: ['qr_code'] });
          const barcodes = await barcodeDetector.detect(canvas);
          if (barcodes.length > 0) {
            const qrValue = barcodes[0].rawValue;
            handleQRCodeDetected(qrValue);
            return;
          }
        } catch (e) {
          console.log('BarcodeDetector error:', e);
        }
      }
    }

    if (scanning) {
      requestAnimationFrame(scanQRCode);
    }
  };

  // Processar QR Code detectado
  const handleQRCodeDetected = async (qrValue) => {
    stopCamera();
    await submitVerification(qrValue);
  };

  // Submeter verificação manual
  const handleManualSubmit = async () => {
    if (!manualCode.trim()) {
      setError('Digite o código');
      return;
    }
    await submitVerification(manualCode.trim());
  };

  // Submeter verificação ao backend
  const submitVerification = async (code) => {
    if (!currentLocation) {
      setError('Aguardando localização GPS...');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      let endpoint = '';
      let payload = {
        order_id: orderId,
        driver_location: currentLocation
      };

      switch (step) {
        case 'pickup_store':
          endpoint = '/delivery/pickup/start';
          payload.store_qr_token = code;
          break;
        case 'pickup_product':
          endpoint = '/delivery/pickup/verify-product';
          payload.product_qr_token = code;
          break;
        case 'dropoff_arrive':
          endpoint = '/delivery/dropoff/arrive';
          break;
        case 'dropoff_client':
          endpoint = '/delivery/dropoff/verify-client';
          payload.client_qr_token = code;
          break;
        case 'dropoff_product_client':
          endpoint = '/delivery/dropoff/verify-product-by-client';
          payload.product_qr_token = code;
          payload.method = useManualInput ? 'pin' : 'qr';
          break;
        default:
          setError('Etapa desconhecida');
          return;
      }

      const response = await api.post(endpoint, payload);
      setSuccess(response.data.message);
      
      setTimeout(() => {
        onSuccess(response.data);
      }, 1500);

    } catch (err) {
      setError(err.response?.data?.detail || 'Erro na verificação');
      setSubmitting(false);
    }
  };

  // Confirmar chegada (não precisa de QR)
  const handleArriveConfirm = async () => {
    await submitVerification('');
  };

  // Obter informações da etapa
  const getStepInfo = () => {
    switch (step) {
      case 'pickup_store':
        return {
          title: 'Escanear QR da Loja',
          description: 'Aponte a câmera para o QR Code fixo da loja',
          icon: <FiShield className="text-purple-500" />,
          color: 'purple',
          requiresScan: true
        };
      case 'pickup_product':
        return {
          title: 'Escanear QR do Produto',
          description: 'Peça ao lojista para mostrar o QR do pedido',
          icon: <FiShield className="text-blue-500" />,
          color: 'blue',
          requiresScan: true
        };
      case 'dropoff_arrive':
        return {
          title: 'Confirmar Chegada',
          description: 'Confirme que você chegou no endereço do cliente',
          icon: <FiMapPin className="text-orange-500" />,
          color: 'orange',
          requiresScan: false
        };
      case 'dropoff_client':
        return {
          title: 'Verificar Cliente',
          description: 'Escaneie o QR Code que o cliente vai mostrar',
          icon: <FiShield className="text-green-500" />,
          color: 'green',
          requiresScan: true
        };
      case 'dropoff_product_client':
        return {
          title: 'Confirmar Recebimento',
          description: 'Cliente: escaneie o QR do produto ou digite o PIN',
          icon: <FiCheck className="text-green-500" />,
          color: 'green',
          requiresScan: true
        };
      default:
        return { title: 'Verificação', description: '', icon: null, color: 'gray', requiresScan: true };
    }
  };

  const stepInfo = getStepInfo();

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="handshake-scanner">
      {/* Header */}
      <div className="bg-gray-900 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {stepInfo.icon}
          <div>
            <h2 className="text-white font-bold">{stepInfo.title}</h2>
            <p className="text-gray-400 text-sm">{stepInfo.description}</p>
          </div>
        </div>
        <button 
          onClick={() => { stopCamera(); onCancel(); }}
          className="p-2 text-white hover:bg-white/10 rounded-full"
        >
          <FiX size={24} />
        </button>
      </div>

      {/* GPS Status */}
      <div className={`px-4 py-2 flex items-center gap-2 text-sm ${
        locationError ? 'bg-red-600' : distance !== null ? 
          (distance <= 50 ? 'bg-green-600' : 'bg-yellow-600') : 'bg-gray-700'
      }`}>
        <FiNavigation className="text-white" />
        {locationError ? (
          <span className="text-white">{locationError}</span>
        ) : distance !== null ? (
          <span className="text-white">
            📍 Distância: <strong>{distance}m</strong>
            {distance <= 50 ? ' ✓ Dentro do raio' : ' ⚠ Aproxime-se mais'}
          </span>
        ) : (
          <span className="text-white">Obtendo localização...</span>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {/* Mensagens */}
        {error && (
          <div className="w-full max-w-sm mb-4 p-4 bg-red-900/80 border border-red-500 rounded-xl flex items-center gap-3">
            <FiAlertTriangle className="text-red-400 flex-shrink-0" />
            <span className="text-red-200">{error}</span>
          </div>
        )}

        {success && (
          <div className="w-full max-w-sm mb-4 p-4 bg-green-900/80 border border-green-500 rounded-xl flex items-center gap-3">
            <FiCheck className="text-green-400 flex-shrink-0" />
            <span className="text-green-200">{success}</span>
          </div>
        )}

        {/* Área de Scanner ou Confirmação */}
        {!stepInfo.requiresScan ? (
          // Confirmação de chegada (sem QR)
          <div className="w-full max-w-sm text-center">
            <div className="bg-gray-800 rounded-2xl p-8 mb-6">
              <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiMapPin className="text-orange-400 text-4xl" />
              </div>
              <h3 className="text-white text-xl font-bold mb-2">Você chegou?</h3>
              <p className="text-gray-400 mb-4">
                Confirme que você está no endereço de entrega para liberar a verificação do cliente.
              </p>
              {distance !== null && (
                <p className={`text-lg font-bold ${distance <= 50 ? 'text-green-400' : 'text-yellow-400'}`}>
                  {distance}m do destino
                </p>
              )}
            </div>

            <button
              onClick={handleArriveConfirm}
              disabled={submitting || !currentLocation || distance > 200}
              className="w-full py-4 bg-orange-500 text-white rounded-xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Verificando...' : 'Confirmar Chegada'}
            </button>

            {distance > 200 && (
              <p className="text-yellow-400 text-sm mt-2">
                Aproxime-se mais do endereço (máx. 200m)
              </p>
            )}
          </div>
        ) : scanning ? (
          // Scanner de câmera ativo
          <div className="w-full max-w-sm">
            <div className="relative bg-black rounded-2xl overflow-hidden aspect-square mb-4">
              <video 
                ref={videoRef} 
                className="w-full h-full object-cover"
                playsInline
                muted
              />
              <canvas ref={canvasRef} className="hidden" />
              
              {/* Overlay de escaneamento */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-48 h-48 border-4 border-white/50 rounded-2xl relative">
                  <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-white rounded-tl-lg" />
                  <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-white rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-white rounded-bl-lg" />
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-white rounded-br-lg" />
                  
                  {/* Linha de scan animada */}
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-green-400 to-transparent animate-pulse" 
                    style={{ animation: 'scan 2s linear infinite' }} 
                  />
                </div>
              </div>
            </div>

            <p className="text-gray-400 text-center mb-4">
              Posicione o QR Code dentro do quadro
            </p>

            <button
              onClick={stopCamera}
              className="w-full py-3 bg-gray-700 text-white rounded-xl"
            >
              Cancelar Scanner
            </button>
          </div>
        ) : useManualInput ? (
          // Entrada manual de código
          <div className="w-full max-w-sm">
            <div className="bg-gray-800 rounded-2xl p-6 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <FiType className="text-gray-400" />
                <span className="text-white font-medium">Digite o código manualmente</span>
              </div>
              
              <input
                type="text"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                placeholder={step === 'dropoff_client' || step === 'dropoff_product_client' ? 'PIN de 6 dígitos' : 'Código do QR'}
                className="w-full px-4 py-4 bg-gray-700 border border-gray-600 rounded-xl text-white text-center text-2xl font-mono tracking-widest focus:border-blue-500 focus:outline-none"
                maxLength={step === 'dropoff_client' || step === 'dropoff_product_client' ? 6 : 50}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setUseManualInput(false)}
                className="flex-1 py-3 bg-gray-700 text-white rounded-xl"
              >
                Usar Câmera
              </button>
              <button
                onClick={handleManualSubmit}
                disabled={submitting || !manualCode.trim()}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl disabled:opacity-50"
              >
                {submitting ? 'Verificando...' : 'Verificar'}
              </button>
            </div>
          </div>
        ) : (
          // Opções iniciais
          <div className="w-full max-w-sm space-y-4">
            <button
              onClick={startCamera}
              className={`w-full py-6 bg-${stepInfo.color}-600 text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-3`}
              style={{ backgroundColor: stepInfo.color === 'purple' ? '#9333ea' : 
                       stepInfo.color === 'blue' ? '#2563eb' : 
                       stepInfo.color === 'green' ? '#16a34a' : '#f97316' }}
            >
              <FiCamera size={24} />
              Abrir Scanner de QR
            </button>

            <button
              onClick={() => setUseManualInput(true)}
              className="w-full py-4 bg-gray-700 text-white rounded-xl flex items-center justify-center gap-2"
            >
              <FiType />
              Digitar código manualmente
            </button>

            <p className="text-gray-500 text-center text-sm">
              {step === 'dropoff_client' ? 
                'Peça ao cliente para mostrar seu QR ou informe o PIN de 6 dígitos' :
                step === 'dropoff_product_client' ?
                'Cliente: escaneie o QR do produto para confirmar recebimento' :
                'Use a câmera para escanear o QR Code ou digite o código manualmente'
              }
            </p>
          </div>
        )}
      </div>

      {/* Estilos de animação */}
      <style>{`
        @keyframes scan {
          0% { top: 0; }
          50% { top: calc(100% - 4px); }
          100% { top: 0; }
        }
      `}</style>
    </div>
  );
};

export default HandshakeScanner;
